import { uiText, useUiLanguage } from '@/i18n/interface';
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar, CalendarPlus } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface RecurringScheduleDialogProps {
  onScheduleCreated: () => void;
}

const daysOfWeek = [
  { id: 1, get name() { return uiText("Monday"); }, get short() { return uiText("Mon"); } },
  { id: 2, get name() { return uiText("Tuesday"); }, get short() { return uiText("Tue"); } },
  { id: 3, get name() { return uiText("Wednesday"); }, get short() { return uiText("Wed"); } },
  { id: 4, get name() { return uiText("Thursday"); }, get short() { return uiText("Thu"); } },
  { id: 5, get name() { return uiText("Friday"); }, get short() { return uiText("Fri"); } },
  { id: 6, get name() { return uiText("Saturday"); }, get short() { return uiText("Sat"); } },
  { id: 0, get name() { return uiText("Sunday"); }, get short() { return uiText("Sun"); } }
];

const RecurringScheduleDialog: React.FC<RecurringScheduleDialogProps> = ({ onScheduleCreated }) => {
  useUiLanguage();
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startTime: '',
    endTime: '',
    maxParticipants: '',
    ageGroup: 'all',
    selectedDays: [] as number[],
    startDate: '',
    endDate: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { profile } = useAuth();

  const handleDayToggle = (dayId: number) => {
    setFormData(prev => ({
      ...prev,
      selectedDays: prev.selectedDays.includes(dayId)
        ? prev.selectedDays.filter(id => id !== dayId)
        : [...prev.selectedDays, dayId]
    }));
  };

  const generateRecurringSessions = async () => {
    if (!formData.title || !formData.startTime || !formData.endTime || !formData.startDate || formData.selectedDays.length === 0) {
      toast({
        title: uiText("Error"),
        description: uiText("Please fill in all required fields"),
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const sessions = [];
      const startDate = new Date(formData.startDate);
      const endDate = formData.endDate ? new Date(formData.endDate) : new Date(startDate.getTime() + (90 * 24 * 60 * 60 * 1000)); // Default 3 months

      // Generate sessions for each selected day of the week
      for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
        if (formData.selectedDays.includes(date.getDay())) {
          sessions.push({
            title: formData.title,
            description: formData.description || null,
            date: date.toISOString().split('T')[0],
            start_time: formData.startTime,
            end_time: formData.endTime,
            max_participants: formData.maxParticipants ? parseInt(formData.maxParticipants) : null,
            age_group: formData.ageGroup,
            instructor_id: profile?.id,
            dojo_id: profile?.dojo_id
          });
        }
      }

      // Insert all sessions in batches
      const batchSize = 50;
      for (let i = 0; i < sessions.length; i += batchSize) {
        const batch = sessions.slice(i, i + batchSize);
        const { error } = await supabase
          .from('training_sessions')
          .insert(batch);

        if (error) throw error;
      }

      toast({
        title: uiText("Success"),
        description: uiText("Created {{value0}} recurring training sessions", { value0: sessions.length }),
      });

      // Reset form
      setFormData({
        title: '',
        description: '',
        startTime: '',
        endTime: '',
        maxParticipants: '',
        ageGroup: 'all',
        selectedDays: [],
        startDate: '',
        endDate: ''
      });

      setOpen(false);
      onScheduleCreated();
    } catch (error) {
      console.error('Error creating recurring sessions:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to create recurring sessions"),
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <CalendarPlus className="h-4 w-4" />
          {uiText("Create Recurring Schedule")}</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            {uiText("Create Recurring Training Schedule")}</DialogTitle>
          <DialogDescription>
            {uiText("Set up a weekly recurring schedule for training sessions. All future sessions will automatically send notifications 10 hours before training.")}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="title">{uiText("Session Title *")}</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder={uiText("e.g., Adults Training")}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ageGroup">{uiText("Age Group")}</Label>
              <Select value={formData.ageGroup} onValueChange={(value) => setFormData(prev => ({ ...prev, ageGroup: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{uiText("All Ages")}</SelectItem>
                  <SelectItem value="kids">{uiText("Kids (5-12)")}</SelectItem>
                  <SelectItem value="teens">{uiText("Teens (13-17)")}</SelectItem>
                  <SelectItem value="adults">{uiText("Adults (18+)")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">{uiText("Description")}</Label>
            <Input
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder={uiText("Optional description")}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startTime">{uiText("Start Time *")}</Label>
              <Input
                id="startTime"
                type="time"
                value={formData.startTime}
                onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endTime">{uiText("End Time *")}</Label>
              <Input
                id="endTime"
                type="time"
                value={formData.endTime}
                onChange={(e) => setFormData(prev => ({ ...prev, endTime: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="maxParticipants">{uiText("Max Participants")}</Label>
              <Input
                id="maxParticipants"
                type="number"
                value={formData.maxParticipants}
                onChange={(e) => setFormData(prev => ({ ...prev, maxParticipants: e.target.value }))}
                placeholder={uiText("Optional")}
                min="1"
              />
            </div>
          </div>

          <div className="space-y-3">
            <Label>{uiText("Select Days of the Week *")}</Label>
            <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
              {daysOfWeek.map((day) => (
                <div key={day.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`day-${day.id}`}
                    checked={formData.selectedDays.includes(day.id)}
                    onCheckedChange={() => handleDayToggle(day.id)}
                  />
                  <Label htmlFor={`day-${day.id}`} className="text-sm cursor-pointer">
                    {day.short}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startDate">{uiText("Start Date *")}</Label>
              <Input
                id="startDate"
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDate">{uiText("End Date (Optional)")}</Label>
              <Input
                id="endDate"
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                min={formData.startDate || new Date().toISOString().split('T')[0]}
              />
              <p className="text-xs text-muted-foreground">
                {uiText("Leave empty to create sessions for 3 months")}</p>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              onClick={generateRecurringSessions}
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? uiText("Creating Sessions...") : uiText("Create Recurring Schedule")}
            </Button>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isSubmitting}
            >
              {uiText("Cancel")}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RecurringScheduleDialog;